import type {
    CreatePromotionPathRuleDTO,
    CreatePromotionRecordDTO,
    ListPromotionHistoryQuery,
    UpdatePromotionRecordDTO,
    ListPromotionHistoryResult,
    ListPromotionPathRulesQuery,
    ListPromotionPathRulesResult,
    ListPromotionRecordsQuery,
    ListPromotionRecordsResult,
    PromotionKpiCheckDTO,
    PromotionPathRuleDetailDTO,
    PromotionRecordDetailDTO,
    UpdatePromotionPathRuleDTO,
} from '@/modules/hr/promotion/domain/types';
import type { OffsetPageDTO } from '@/shared/contracts/base';
import { config } from '@/shared/lib/config';
import { fetcher } from '@/shared/lib/fetcher';

const base = `${config.apiUrl}/hr/promotion`;

function buildQueryString(
    params: Record<string, string | number | undefined | null>,
): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.set(key, String(value));
        }
    });
    const query = search.toString();
    return query ? `?${query}` : '';
}

export type LookupItem = {
    id: string;
    label: string;
    subLabel?: string | null;
};

export const promotionApi = {
    listRules(
        query: ListPromotionPathRulesQuery,
    ): Promise<ListPromotionPathRulesResult> {
        return fetcher.get<ListPromotionPathRulesResult>(
            `${base}/rules${buildQueryString({
                offset: query.offset,
                limit: query.limit,
                sort: query.sort ? JSON.stringify(query.sort) : undefined,
                isActive:
                    query.isActive == null ? undefined : String(query.isActive),
                fromPositionId: query.fromPositionId,
                toPositionId: query.toPositionId,
                search: query.search,
            })}`,
        );
    },

    getRuleById(id: string): Promise<PromotionPathRuleDetailDTO> {
        return fetcher.get<PromotionPathRuleDetailDTO>(`${base}/rules/${id}`);
    },

    createRule(dto: CreatePromotionPathRuleDTO): Promise<{ id: string }> {
        return fetcher.post<{ id: string }>(`${base}/rules`, dto);
    },

    updateRule(
        id: string,
        dto: UpdatePromotionPathRuleDTO,
    ): Promise<{ ok: boolean }> {
        return fetcher.patch<{ ok: boolean }>(`${base}/rules/${id}`, dto);
    },

    deleteRule(id: string): Promise<{ ok: boolean }> {
        return fetcher.delete<{ ok: boolean }>(`${base}/rules/${id}`);
    },

    listRecords(
        query: ListPromotionRecordsQuery,
    ): Promise<ListPromotionRecordsResult> {
        return fetcher.get<ListPromotionRecordsResult>(
            `${base}/records${buildQueryString({
                offset: query.offset,
                limit: query.limit,
                sort: query.sort ? JSON.stringify(query.sort) : undefined,
                employeeId: query.employeeId,
                periodYear: query.periodYear,
                periodMonth: query.periodMonth,
                positionId: query.positionId,
                search: query.search,
            })}`,
        );
    },

    getRecordById(id: string): Promise<PromotionRecordDetailDTO> {
        return fetcher.get<PromotionRecordDetailDTO>(`${base}/records/${id}`);
    },

    getKpiCheckPreview(params: {
        employeeId: string;
        positionId?: string | null;
        referenceDate?: string | null;
    }): Promise<{
        kpiCheck: PromotionKpiCheckDTO | null;
        kpiCheckMode: 'mandatory' | 'warning' | 'off';
    }> {
        const search = new URLSearchParams();
        search.set('employeeId', params.employeeId);
        if (params.positionId) search.set('positionId', params.positionId);
        if (params.referenceDate) search.set('referenceDate', params.referenceDate);
        return fetcher.get(
            `${base}/kpi-check-preview?${search.toString()}`,
        );
    },

    createRecord(dto: CreatePromotionRecordDTO): Promise<{ id: string }> {
        return fetcher.post<{ id: string }>(`${base}/records`, dto);
    },

    approveRecord(id: string): Promise<{ ok: boolean }> {
        return fetcher.post<{ ok: boolean }>(`${base}/records/${encodeURIComponent(id)}/approve`);
    },

    updateRecord(
        id: string,
        dto: UpdatePromotionRecordDTO,
    ): Promise<{ ok: boolean }> {
        return fetcher.patch<{ ok: boolean }>(
            `${base}/records/${encodeURIComponent(id)}`,
            dto,
        );
    },

    listHistory(
        query: ListPromotionHistoryQuery,
    ): Promise<ListPromotionHistoryResult> {
        return fetcher.get<ListPromotionHistoryResult>(
            `${base}/history${buildQueryString({
                offset: query.offset,
                limit: query.limit,
                sort: query.sort ? JSON.stringify(query.sort) : undefined,
                employeeId: query.employeeId,
                periodYear: query.periodYear,
                periodMonth: query.periodMonth,
                positionId: query.positionId,
                search: query.search,
            })}`,
        );
    },

    async listEmployees(limit = 200): Promise<LookupItem[]> {
        const result = await fetcher.get<
            OffsetPageDTO<{ id: string; name: string; email: string }>
        >(`${config.apiUrl}/users?limit=${limit}&offset=0`);
        return (result.data ?? []).map((item) => ({
            id: item.id,
            label: item.name || item.email,
            subLabel: item.email,
        }));
    },

    async listPositions(limit = 200): Promise<LookupItem[]> {
        const result = await fetcher.get<
            OffsetPageDTO<{ id: string; title: string }>
        >(`${config.apiUrl}/positions?limit=${limit}&offset=0`);
        return (result.data ?? []).map((item) => ({
            id: item.id,
            label: item.title,
        }));
    },

    async listLevels(limit = 200): Promise<LookupItem[]> {
        const result = await fetcher.get<
            OffsetPageDTO<{ id: string; name: string }>
        >(`${base}/levels?limit=${limit}&offset=0`);
        return (result.data ?? []).map((item) => ({
            id: item.id,
            label: item.name,
        }));
    },

    async getEmployeeCurrentPosition(
        employeeId: string,
    ): Promise<{ positionId: string | null; positionTitle: string | null }> {
        return fetcher.get<{ positionId: string | null; positionTitle: string | null }>(
            `${base}/employees/${encodeURIComponent(employeeId)}/current-position`,
        );
    },
};
